import { normalizeNumberInput } from "@/lib/parse-number";
import {
  absBig,
  BIG_ONE,
  BIG_TEN,
  BIG_TWO,
  BIG_ZERO,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";
import { toSuperscript } from "@/lib/tool-kit/math/primes";

/**
 * Significant figures, done on the digits rather than on a float.
 *
 * A float cannot answer the question at all: `Number("2.50")` is 2.5, so the trailing
 * zero — the whole point of writing 2.50 — is gone before any rule can look at it. And
 * rounding through floats prints 0.30000000000000004. So every number here is kept as
 * the string of digits that was typed plus a power of ten, and rounding and arithmetic
 * happen on BigInt digit strings.
 *
 * The rules are the ones taught in general chemistry and physics:
 *
 * - nonzero digits are significant;
 * - zeros between nonzero digits ("captive" zeros) are significant;
 * - leading zeros are never significant — they only place the decimal point;
 * - trailing zeros are significant when the number has a decimal point (2.50, 1200.),
 *   and ambiguous when it does not (1200 has two, three or four). The ambiguous zeros
 *   are counted as not significant, the usual convention, and reported as ambiguous.
 */

export type DigitKind =
  | "nonzero"
  | "leading"
  | "captive"
  | "trailing-point"
  | "trailing-ambiguous";

export interface DigitInfo {
  digit: string;
  kind: DigitKind;
  significant: boolean;
  /** The digit's power of ten: 2 for hundreds, −1 for tenths. */
  place: number;
}

export interface SigFigNumber {
  negative: boolean;
  /** The mantissa as read, sign removed and decimal comma resolved: "0.004050". */
  mantissa: string;
  /** The power of ten from scientific or E-notation; 0 for a plain number. */
  exponent: number;
  scientific: boolean;
  hasPoint: boolean;
  digits: DigitInfo[];
  /** Significant figures, not counting ambiguous trailing zeros. */
  sigFigs: number;
  /** Trailing zeros in a whole number without a decimal point. */
  ambiguousZeros: number;
  /** Every digit is zero. */
  isZero: boolean;
  /** Signed digits of the mantissa as an integer; value = coefficient × 10^scale. */
  coefficient: bigint;
  scale: number;
  /** Place of the last significant digit (of the last written digit, for zero). */
  lastPlace: number;
  /** Digits after the decimal point once the exponent is applied. */
  decimalPlaces: number;
  /** A lone comma before three digits — "1,200" — was read as a decimal comma. */
  commaAmbiguity: boolean;
}

export type SigParseError = "empty" | "invalid" | "too-long";

const MAX_DIGITS = 60;
const MAX_EXPONENT = 400;

const UNICODE_MINUS = /[−‒–]/g;
const SUPERSCRIPT_RUN = /10\s*([⁺⁻]?[⁰¹²³⁴⁵⁶⁷⁸⁹]+)/;
const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const TIMES_TEN =
  /^(.+?)\s*[x×*·⋅]\s*10\s*(?:\^|\*\*)\s*\(?\s*([+-]?\d+)\s*\)?$/i;
const E_NOTATION = /^(.+?)\s*[eE]\s*([+-]?\d+)$/;
const LONE_COMMA_GROUP = /^[+-]?\d{1,3},\d{3}$/;
const SIGN = /^[+-]/;
const TRAILING_ZEROS = /0+$/;
const NONZERO_DIGIT = /[1-9]/;
const NON_DIGIT = /\D/g;
const WHITESPACE = /\s/g;

/** "10⁻³" → "10^-3", so one pattern reads both ways of writing the exponent. */
function unSuperscript(text: string): string {
  return text.replace(SUPERSCRIPT_RUN, (_, run: string) => {
    const mapped = [...run]
      .map((ch) => {
        if (ch === "⁻") {
          return "-";
        }
        if (ch === "⁺") {
          return "+";
        }
        return String(SUPERSCRIPTS.indexOf(ch));
      })
      .join("");
    return `10^${mapped}`;
  });
}

/** Split typed text into a mantissa and a power of ten. */
function splitNotation(text: string) {
  const times = TIMES_TEN.exec(text);
  if (times) {
    return {
      raw: times[1].trim(),
      exponent: Number.parseInt(times[2], 10),
      scientific: true,
    };
  }
  const eMatch = E_NOTATION.exec(text);
  if (eMatch) {
    return {
      raw: eMatch[1].trim(),
      exponent: Number.parseInt(eMatch[2], 10),
      scientific: true,
    };
  }
  return { raw: text, exponent: 0, scientific: false };
}

function classifyDigit(
  index: number,
  first: number,
  last: number,
  hasPoint: boolean,
  digit: string
): DigitKind {
  if (digit !== "0") {
    return "nonzero";
  }
  if (first === -1 || index < first) {
    return "leading";
  }
  if (index < last) {
    return "captive";
  }
  return hasPoint ? "trailing-point" : "trailing-ambiguous";
}

function lastNonzeroIndex(digits: string): number {
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    if (digits[i] !== "0") {
      return i;
    }
  }
  return -1;
}

/** Classify every digit of a validated machine-decimal mantissa. */
function analyse(
  normalized: string,
  exponent: number,
  scientific: boolean,
  commaAmbiguity: boolean
): SigFigNumber {
  const negative = normalized.startsWith("-");
  const body = normalized.replace(SIGN, "");
  const hasPoint = body.includes(".");
  const allDigits = body.replace(".", "");
  const integerLength = hasPoint ? body.indexOf(".") : allDigits.length;
  const first = allDigits.search(NONZERO_DIGIT);
  const last = lastNonzeroIndex(allDigits);
  const digits: DigitInfo[] = [...allDigits].map((digit, index) => {
    const kind = classifyDigit(index, first, last, hasPoint, digit);
    return {
      digit,
      kind,
      significant: kind !== "leading" && kind !== "trailing-ambiguous",
      place: integerLength - 1 - index + exponent,
    };
  });
  const isZero = first === -1;
  const significant = digits.filter((d) => d.significant);
  const lastWritten = digits.at(-1)?.place ?? exponent;
  const magnitude = BigInt(allDigits);
  return {
    negative: negative && !isZero,
    mantissa: body,
    exponent,
    scientific,
    hasPoint,
    digits,
    sigFigs: significant.length,
    ambiguousZeros: digits.filter((d) => d.kind === "trailing-ambiguous")
      .length,
    isZero,
    coefficient: negative ? -magnitude : magnitude,
    scale: exponent - (allDigits.length - integerLength),
    lastPlace: isZero
      ? lastWritten
      : (significant.at(-1)?.place ?? lastWritten),
    decimalPlaces: Math.max(0, -lastWritten),
    commaAmbiguity,
  };
}

/**
 * Read a typed number for significant-figure work: `0.004050`, `1200.`, `−3.40`,
 * `6.02e23`, `6.02 × 10^23`, `6.02×10²³`, `2,50` (decimal comma).
 */
export function parseSigFigNumber(
  raw: string
): SigFigNumber | { error: SigParseError } {
  const text = unSuperscript(raw.trim().replace(UNICODE_MINUS, "-"));
  if (text === "") {
    return { error: "empty" };
  }
  const { raw: mantissaRaw, exponent, scientific } = splitNotation(text);
  const normalized = normalizeNumberInput(mantissaRaw);
  if (normalized === null || !Number.isFinite(exponent)) {
    return { error: "invalid" };
  }
  const digitCount = normalized.replace(NON_DIGIT, "").length;
  if (digitCount > MAX_DIGITS || Math.abs(exponent) > MAX_EXPONENT) {
    return { error: "too-long" };
  }
  return analyse(
    normalized,
    exponent,
    scientific,
    LONE_COMMA_GROUP.test(mantissaRaw.replace(WHITESPACE, ""))
  );
}

/* --------------------------------------------------------------- rounding */

/**
 * A rounded result: `coefficient × 10^lastPlace`, where every digit of `coefficient`
 * is significant. 2.50 is { coefficient: 250, lastPlace: −2 }.
 */
export interface SigValue {
  negative: boolean;
  coefficient: bigint;
  lastPlace: number;
}

export type RoundingMode = "half-up" | "half-even";

export function sigCount(value: SigValue): number {
  return value.coefficient === BIG_ZERO
    ? 0
    : value.coefficient.toString().length;
}

/** An exact signed decimal: coefficient × 10^scale. */
export interface ExactDecimal {
  coefficient: bigint;
  scale: number;
}

/** Keep the digits of `value` down to the 10^place position, rounding the rest. */
export function roundAtPlace(
  value: ExactDecimal,
  place: number,
  mode: RoundingMode
): SigValue {
  const negative = value.coefficient < BIG_ZERO;
  const magnitude = absBig(value.coefficient);
  if (value.scale >= place) {
    const coefficient = magnitude * pow10Big(value.scale - place);
    return {
      negative: negative && coefficient !== BIG_ZERO,
      coefficient,
      lastPlace: place,
    };
  }
  const divisor = pow10Big(place - value.scale);
  let kept = magnitude / divisor;
  const twiceRest = (magnitude % divisor) * BIG_TWO;
  const isOdd = kept % BIG_TWO === BIG_ONE;
  if (
    twiceRest > divisor ||
    (twiceRest === divisor && (mode === "half-up" || isOdd))
  ) {
    kept += BIG_ONE;
  }
  return {
    negative: negative && kept !== BIG_ZERO,
    coefficient: kept,
    lastPlace: place,
  };
}

/** The power of ten of the first nonzero digit. */
function leadingPlace(value: ExactDecimal): number {
  return value.scale + absBig(value.coefficient).toString().length - 1;
}

/** Round to `n` significant figures, padding with significant zeros if needed. */
export function roundToSigFigs(
  value: ExactDecimal,
  n: number,
  mode: RoundingMode
): SigValue {
  if (value.coefficient === BIG_ZERO) {
    return { negative: false, coefficient: BIG_ZERO, lastPlace: 0 };
  }
  const place = leadingPlace(value) - n + 1;
  const result = roundAtPlace(value, place, mode);
  // 9.99 → 10.0: the carry added a digit, so the last kept place moves up one.
  if (result.coefficient.toString().length > n) {
    return {
      ...result,
      coefficient: result.coefficient / BIG_TEN,
      lastPlace: place + 1,
    };
  }
  return result;
}

export interface RoundExplanation {
  /** The first n significant digits before rounding, e.g. "405". */
  kept: string;
  /** The digit that decides the rounding, or "" when nothing was dropped. */
  decider: string;
  /** Digits dropped beyond the decider are not all zero. */
  restNonzero: boolean;
  roundedUp: boolean;
  /** Zeros added because the number had fewer digits than requested. */
  padded: number;
  /** A carry added a digit (9.96 → 10.0). */
  carried: boolean;
}

/** What `roundToSigFigs` did, digit by digit, for the working. */
export function explainRound(
  value: ExactDecimal,
  n: number,
  mode: RoundingMode
): RoundExplanation {
  const digits = absBig(value.coefficient).toString();
  if (digits.length <= n) {
    return {
      kept: digits,
      decider: "",
      restNonzero: false,
      roundedUp: false,
      padded: n - digits.length,
      carried: false,
    };
  }
  const kept = digits.slice(0, n);
  const decider = digits[n];
  const restNonzero = digits.slice(n + 1).replace(TRAILING_ZEROS, "") !== "";
  const rounded = roundToSigFigs(value, n, mode);
  const roundedUp =
    rounded.coefficient.toString() !== kept ||
    rounded.lastPlace !== leadingPlace(value) - n + 1;
  return {
    kept,
    decider,
    restNonzero,
    roundedUp,
    padded: 0,
    carried: rounded.lastPlace !== leadingPlace(value) - n + 1,
  };
}

/* ------------------------------------------------------------- formatting */

export interface PlainText {
  text: string;
  /** Written out, the trailing zeros would read as fewer significant figures. */
  ambiguous: boolean;
  /** A trailing decimal point marks the zeros before it as significant: "120." */
  trailingPoint: boolean;
}

/** Longest plain decimal worth writing out; beyond it, scientific notation only. */
export const MAX_PLAIN_PLACES = 30;

export function canWritePlain(value: SigValue): boolean {
  const digits = value.coefficient.toString().length;
  return (
    value.lastPlace <= MAX_PLAIN_PLACES &&
    value.lastPlace + digits >= -MAX_PLAIN_PLACES
  );
}

/** The value as an ordinary decimal, with its significant zeros visible. */
export function formatPlain(value: SigValue, decimalMark = "."): PlainText {
  const digits = value.coefficient.toString();
  const sign = value.negative ? "−" : "";
  if (value.lastPlace < 0) {
    const places = -value.lastPlace;
    const padded = digits.padStart(places + 1, "0");
    return {
      text: `${sign}${padded.slice(0, -places)}${decimalMark}${padded.slice(-places)}`,
      ambiguous: false,
      trailingPoint: false,
    };
  }
  const text = `${digits}${"0".repeat(value.lastPlace)}`;
  if (value.coefficient === BIG_ZERO) {
    return { text: "0", ambiguous: false, trailingPoint: false };
  }
  const visible = text.replace(TRAILING_ZEROS, "").length;
  if (visible === digits.length) {
    return { text: `${sign}${text}`, ambiguous: false, trailingPoint: false };
  }
  if (value.lastPlace === 0) {
    return {
      text: `${sign}${text}${decimalMark}`,
      ambiguous: false,
      trailingPoint: true,
    };
  }
  return { text: `${sign}${text}`, ambiguous: true, trailingPoint: false };
}

export interface ScientificParts {
  sign: string;
  mantissa: string;
  exponent: number;
}

/** 0.004050 → { mantissa: "4.050", exponent: −3 }. */
export function scientificParts(
  value: SigValue,
  decimalMark = "."
): ScientificParts {
  const sign = value.negative ? "−" : "";
  const digits = value.coefficient.toString();
  if (value.coefficient === BIG_ZERO) {
    return { sign: "", mantissa: "0", exponent: 0 };
  }
  const tail = digits.length > 1 ? `${decimalMark}${digits.slice(1)}` : "";
  return {
    sign,
    mantissa: `${digits[0]}${tail}`,
    exponent: value.lastPlace + digits.length - 1,
  };
}

/** "4.050 × 10⁻³" */
export function formatScientific(value: SigValue, decimalMark = "."): string {
  const parts = scientificParts(value, decimalMark);
  return `${parts.sign}${parts.mantissa} × 10${toSuperscript(parts.exponent)}`;
}

/** "4.050e-3" — always a period, because E-notation is for machines. */
export function formatENotation(value: SigValue): string {
  const parts = scientificParts(value, ".");
  const sign = parts.sign === "" ? "" : "-";
  return `${sign}${parts.mantissa}e${parts.exponent}`;
}

/** Drop trailing fractional zeros: 3.000 → 3. Every digit left is kept as written. */
export function trimExact(value: ExactDecimal): SigValue {
  const negative = value.coefficient < BIG_ZERO;
  let coefficient = absBig(value.coefficient);
  let scale = value.scale;
  while (
    scale < 0 &&
    coefficient % BIG_TEN === BIG_ZERO &&
    coefficient !== BIG_ZERO
  ) {
    coefficient /= BIG_TEN;
    scale += 1;
  }
  return {
    negative: negative && coefficient !== BIG_ZERO,
    coefficient,
    lastPlace: scale,
  };
}

/** An exact decimal written in full (trailing fractional zeros trimmed), like a calculator. */
export function formatExact(value: ExactDecimal, decimalMark = "."): string {
  const asSig = trimExact(value);
  const { negative, coefficient, lastPlace: scale } = asSig;
  if (!canWritePlain(asSig)) {
    return formatScientific(asSig, decimalMark);
  }
  if (scale >= 0) {
    const sign = negative && coefficient !== BIG_ZERO ? "−" : "";
    return `${sign}${coefficient.toString()}${"0".repeat(scale)}`;
  }
  return formatPlain(asSig, decimalMark).text;
}

/** A parsed number's exact value. */
export function exactOf(number: SigFigNumber): ExactDecimal {
  return { coefficient: number.coefficient, scale: number.scale };
}

/** The parsed number as a SigValue of its own significant digits. */
export function sigValueOf(number: SigFigNumber): SigValue {
  return roundAtPlace(exactOf(number), number.lastPlace, "half-up");
}

/* ------------------------------------------------------------- arithmetic */

export type SigOperation = "add" | "subtract" | "multiply" | "divide";

export interface SigOperand {
  number: SigFigNumber;
  /** A counted or defined number: infinitely many significant figures. */
  exact: boolean;
}

export type SigRule = "precision" | "sig-figs" | "exact" | "zero";

export interface SigArithmeticResult {
  rule: SigRule;
  /** The full-precision answer before any significant-figure rounding. */
  unrounded: ExactDecimal;
  /** True for a division whose decimal does not terminate (shown to 15 digits). */
  unroundedApproximate: boolean;
  /** × ÷: the fewest significant figures among the measured operands. */
  limitingSigFigs: number | null;
  /** + −: the coarsest last-significant place among the measured operands. */
  limitingPlace: number | null;
  /** Which operands (0, 1) set the limit. */
  limitingOperands: number[];
  result: SigValue;
}

const DIVISION_DIGITS = 30;
const EXACT_DISPLAY_DIGITS = 15;

function addExact(
  a: ExactDecimal,
  b: ExactDecimal,
  sign: 1 | -1
): ExactDecimal {
  const scale = Math.min(a.scale, b.scale);
  const left = a.coefficient * pow10Big(a.scale - scale);
  const right = b.coefficient * pow10Big(b.scale - scale);
  return { coefficient: sign === 1 ? left + right : left - right, scale };
}

/**
 * a ÷ b to about thirty significant digits. A nonzero remainder appends a sticky 1, so
 * a later round-half decision never mistakes 0.4999… for exactly one half.
 */
function divideExact(
  a: ExactDecimal,
  b: ExactDecimal
): { value: ExactDecimal; approximate: boolean } {
  const numerator = absBig(a.coefficient);
  const denominator = absBig(b.coefficient);
  const shift = Math.max(
    0,
    DIVISION_DIGITS +
      denominator.toString().length -
      numerator.toString().length
  );
  const scaled = numerator * pow10Big(shift);
  let quotient = scaled / denominator;
  const remainder = scaled % denominator;
  let scale = a.scale - b.scale - shift;
  if (remainder !== BIG_ZERO) {
    quotient = quotient * BIG_TEN + BIG_ONE;
    scale -= 1;
  }
  const negative = a.coefficient < BIG_ZERO !== b.coefficient < BIG_ZERO;
  return {
    value: { coefficient: negative ? -quotient : quotient, scale },
    approximate: remainder !== BIG_ZERO,
  };
}

function unroundedResult(
  a: ExactDecimal,
  b: ExactDecimal,
  operation: SigOperation
): { value: ExactDecimal; approximate: boolean } {
  if (operation === "add" || operation === "subtract") {
    return {
      value: addExact(a, b, operation === "add" ? 1 : -1),
      approximate: false,
    };
  }
  if (operation === "multiply") {
    return {
      value: {
        coefficient: a.coefficient * b.coefficient,
        scale: a.scale + b.scale,
      },
      approximate: false,
    };
  }
  return divideExact(a, b);
}

function measuredIndexes(operands: readonly SigOperand[]): number[] {
  return operands.flatMap((operand, index) => (operand.exact ? [] : [index]));
}

function precisionRule(
  operands: readonly SigOperand[],
  unrounded: ExactDecimal,
  mode: RoundingMode
): Pick<SigArithmeticResult, "limitingPlace" | "limitingOperands" | "result"> {
  const measured = measuredIndexes(operands);
  const place = Math.max(...measured.map((i) => operands[i].number.lastPlace));
  return {
    limitingPlace: place,
    limitingOperands: measured.filter(
      (i) => operands[i].number.lastPlace === place
    ),
    result: roundAtPlace(unrounded, place, mode),
  };
}

function sigFigRule(
  operands: readonly SigOperand[],
  unrounded: ExactDecimal,
  mode: RoundingMode
): Pick<
  SigArithmeticResult,
  "limitingSigFigs" | "limitingOperands" | "result"
> {
  const measured = measuredIndexes(operands);
  const fewest = Math.min(...measured.map((i) => operands[i].number.sigFigs));
  return {
    limitingSigFigs: fewest,
    limitingOperands: measured.filter(
      (i) => operands[i].number.sigFigs === fewest
    ),
    result: roundToSigFigs(unrounded, fewest, mode),
  };
}

/**
 * One operation with the significant-figure rules applied to the answer:
 * × and ÷ keep the fewest significant figures of the measured operands; + and − keep
 * the coarsest last decimal place. Exact numbers never limit either.
 *
 * Null for division by zero.
 */
export function sigArithmetic(
  a: SigOperand,
  b: SigOperand,
  operation: SigOperation,
  mode: RoundingMode
): SigArithmeticResult | null {
  if (operation === "divide" && b.number.isZero) {
    return null;
  }
  const operands = [a, b];
  const { value: unrounded, approximate } = unroundedResult(
    exactOf(a.number),
    exactOf(b.number),
    operation
  );
  const base = {
    unrounded,
    unroundedApproximate: approximate,
    limitingSigFigs: null,
    limitingPlace: null,
    limitingOperands: [],
  };
  const additive = operation === "add" || operation === "subtract";
  const measured = measuredIndexes(operands);
  if (measured.length === 0) {
    const result = approximate
      ? roundToSigFigs(unrounded, EXACT_DISPLAY_DIGITS, mode)
      : trimExact(unrounded);
    return { ...base, rule: "exact", result };
  }
  if (additive) {
    return {
      ...base,
      rule: "precision",
      ...precisionRule(operands, unrounded, mode),
    };
  }
  if (
    unrounded.coefficient === BIG_ZERO ||
    measured.some((i) => operands[i].number.isZero)
  ) {
    return {
      ...base,
      rule: "zero",
      result: { negative: false, coefficient: BIG_ZERO, lastPlace: 0 },
    };
  }
  return {
    ...base,
    rule: "sig-figs",
    ...sigFigRule(operands, unrounded, mode),
  };
}

/** The unrounded answer as a calculator would show it (15 digits for a repeating one). */
export function formatUnrounded(
  result: SigArithmeticResult,
  mode: RoundingMode,
  decimalMark = "."
): string {
  if (!result.unroundedApproximate) {
    return formatExact(result.unrounded, decimalMark);
  }
  const shown = roundToSigFigs(result.unrounded, EXACT_DISPLAY_DIGITS, mode);
  return formatExact(
    {
      coefficient: shown.negative ? -shown.coefficient : shown.coefficient,
      scale: shown.lastPlace,
    },
    decimalMark
  );
}
